/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import Button from 'components/generics/Button/Button';
import Input from 'components/generics/Input/Input';
import InputLabel from 'components/generics/InputLabel/InputLabel';
import Modal from 'components/generics/Modal/Modal';
import ConfirmDelete from 'components/generics/ConfirmDelete/ConfirmDelete';
import useAuth from 'providers/Auth/useAuth';

import {
  getCourses,
  createCourse,
  updateCourse,
  deleteCourse,
  getCourseUsers,
  setCourseUsers,
  duplicateCourse,
} from 'services/API/Course';
import { get as getUsers } from 'services/API/User';

import './Courses.style.scss';

/**
 * Groupes d'entités copiables lors d'une duplication.
 * Chaque clé correspond au champ du DTO DuplicateOptionsDto côté API.
 */
const DUPLICATE_GROUPS = [
  { key: 'flightStatuses', label: 'Statuts de vol' },
  { key: 'delayTypes', label: 'Types de retard' },
  { key: 'aircraftTypes', label: "Types d'appareils" },
  { key: 'airports', label: 'Aéroports' },
  { key: 'flightTimes', label: 'Temps de vol (routes)' },
  { key: 'airlines', label: 'Compagnies' },
  { key: 'aircrafts', label: 'Avions' },
  { key: 'missions', label: 'Missions' },
  { key: 'stages', label: 'Étapes' },
  { key: 'flights', label: 'Vols' },
  { key: 'checklists', label: 'Modèles de checklist' },
  { key: 'delays', label: 'Retards' },
  { key: 'userAccesses', label: 'Accès des utilisateurs' },
];

const DEFAULT_GROUP_STATE = Object.fromEntries(DUPLICATE_GROUPS.map(g => [g.key, false]));

const Courses = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  // userId -> [noms des courses où l'utilisateur est affecté]
  const [assignments, setAssignments] = useState({});

  const [formModal, setFormModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState({ name: '', code: '', year: '' });

  const [confirmDelete, setConfirmDelete] = useState(null);

  const [accessModal, setAccessModal] = useState(null); // course dont on édite les accès
  const [accessUserIds, setAccessUserIds] = useState([]);

  const [duplicateModal, setDuplicateModal] = useState(null); // course source à dupliquer
  const [duplicateForm, setDuplicateForm] = useState({ name: '', code: '', year: '' });
  const [duplicateGroups, setDuplicateGroups] = useState(DEFAULT_GROUP_STATE);

  const isAdmin = ['Admin', 'CPSAdmin'].includes(user?.roleName);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }
    reloadCourses();
    getUsers()
      .then(res => setUsers(res.data || []))
      .catch(err => toast.error('Unable to load users'));
  }, []);

  const loadAssignments = async courseList => {
    try {
      const results = await Promise.all(
        (courseList || []).map(course =>
          getCourseUsers(course.id).then(res => ({ course, rows: res.data || [] })),
        ),
      );
      const map = {};
      results.forEach(({ course, rows }) => {
        rows.forEach(row => {
          map[row.userId] = [...(map[row.userId] || []), course.name];
        });
      });
      setAssignments(map);
    } catch (err) {
      // Non bloquant : l'écran reste utilisable sans les indicateurs d'affectation.
    }
  };

  const reloadCourses = () => {
    getCourses()
      .then(res => {
        const list = res.data || [];
        setCourses(list);
        loadAssignments(list);
      })
      .catch(err => toast.error('Unable to load courses'));
  };

  // -- CRUD ------------------------------------------------------------------
  const openCreate = () => {
    setEditingCourse(null);
    setFormData({ name: '', code: '', year: '' });
    setFormModal(true);
  };

  const openEdit = course => {
    setEditingCourse(course);
    setFormData({ name: course.name, code: course.code ?? '', year: course.year ?? '' });
    setFormModal(true);
  };

  const submitForm = () => {
    const payload = {
      name: formData.name,
      code: formData.code || null,
      year: formData.year ? +formData.year : null,
    };
    const action = editingCourse ? updateCourse(editingCourse.id, payload) : createCourse(payload);
    action
      .then(() => {
        toast.success(editingCourse ? 'Course updated' : 'Course created');
        setFormModal(false);
        reloadCourses();
      })
      .catch(err => toast.error(err?.response?.data?.message ?? 'Error while saving course'));
  };

  const onDelete = () => {
    deleteCourse(confirmDelete.id)
      .then(() => {
        toast.success('Course deleted');
        setConfirmDelete(null);
        reloadCourses();
      })
      .catch(err => toast.error(err?.response?.data?.message ?? 'Error while deleting course'));
  };

  // -- Accès utilisateurs ------------------------------------------------------
  const openAccess = async course => {
    setAccessModal(course);
    try {
      const res = await getCourseUsers(course.id);
      setAccessUserIds(res.data.map(r => r.userId));
    } catch (err) {
      toast.error('Unable to load course users');
    }
  };

  const toggleAccess = userId => {
    setAccessUserIds(prev => (prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]));
  };

  const submitAccess = () => {
    setCourseUsers(accessModal.id, accessUserIds)
      .then(() => {
        toast.success('Access saved');
        setAccessModal(null);
        // Les utilisateurs ajoutés ont pu être retirés d'autres courses.
        loadAssignments(courses);
      })
      .catch(err => toast.error('Unable to save access'));
  };

  // -- Duplication --------------------------------------------------------------
  const openDuplicate = course => {
    setDuplicateModal(course);
    setDuplicateForm({ name: `${course.name} (copie)`, code: course.code ?? '', year: course.year ?? '' });
    setDuplicateGroups({ ...DEFAULT_GROUP_STATE, flightStatuses: true, delayTypes: true, aircraftTypes: true, airports: true, flightTimes: true });
  };

  const toggleGroup = key => {
    setDuplicateGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const submitDuplicate = () => {
    duplicateCourse(duplicateModal.id, {
      name: duplicateForm.name,
      code: duplicateForm.code || null,
      year: duplicateForm.year ? +duplicateForm.year : null,
      copy: duplicateGroups,
    })
      .then(() => {
        toast.success('Course duplicated');
        setDuplicateModal(null);
        reloadCourses();
      })
      .catch(err => toast.error(err?.response?.data?.message ?? 'Error while duplicating course'));
  };

  if (!isAdmin) {
    return (
      <div className="courses">
        <h1>Courses</h1>
        <p>Vous n'avez pas les droits pour gérer les courses.</p>
      </div>
    );
  }

  return (
    <div className="courses">
      <div className="courses__header">
        <div>
          <h1>Courses</h1>
        </div>
        <Button text="Add course" defaultStyle="primary" onClick={openCreate} />
      </div>

      <table className="courses__table">
        <thead>
          <tr>
            <th>Nom</th>
            <th>Code</th>
            <th>Année</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {courses.map(course => (
            <tr key={course.id}>
              <td>{course.name}</td>
              <td>{course.code ?? '-'}</td>
              <td>{course.year ?? '-'}</td>
              <td className="courses__actions">
                <Button text="Users" defaultStyle="primary less_padding" onClick={() => openAccess(course)} />
                <Button text="Duplicate" defaultStyle="primary less_padding" onClick={() => openDuplicate(course)} />
                <Button text="Edit" defaultStyle="primary less_padding" onClick={() => openEdit(course)} />
                <Button text="Delete" defaultStyle="primary less_padding" onClick={() => setConfirmDelete(course)} />
              </td>
            </tr>
          ))}
          {courses.length === 0 && (
            <tr>
              <td colSpan={4}>Aucune course</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* -- Modal création / édition ------------------------------------------ */}
      {formModal && (
        <Modal onClose={() => setFormModal(false)}>
          <div className="modal__content courses__modal">
            <h2>{editingCourse ? 'Edit' : 'Add'} course</h2>
            <InputLabel label="Name" name="name">
              <Input name="name" type="string" onChange={value => setFormData({ ...formData, name: value })} value={formData.name} />
            </InputLabel>
            <InputLabel label="Code" name="code">
              <Input name="code" type="string" onChange={value => setFormData({ ...formData, code: value })} value={formData.code} />
            </InputLabel>
            <InputLabel label="Year" name="year">
              <Input name="year" type="string" onChange={value => setFormData({ ...formData, year: value })} value={String(formData.year ?? '')} />
            </InputLabel>
            <Button text={`${editingCourse ? 'Edit' : 'Add'} course`} defaultStyle="primary" onClick={submitForm} />
          </div>
        </Modal>
      )}

      {/* -- Modal accès utilisateurs ------------------------------------------ */}
      {accessModal && (
        <Modal onClose={() => setAccessModal(null)}>
          <div className="modal__content courses__modal courses__modal--wide">
            <h2>Accès à « {accessModal.name} »</h2>
            <p className="courses__hint">
              Un utilisateur ne peut être affecté qu'à une seule course (sauf Admin / CPSAdmin).
              Sélectionner un utilisateur déjà affecté ailleurs le déplacera vers cette course.
            </p>
            <div className="courses__accessList">
              {users.map(u => {
                const isMultiCourse = ['Admin', 'CPSAdmin'].includes(u.userRole?.name);
                const otherCourses = (assignments[u.id] || []).filter(name => name !== accessModal.name);
                return (
                  <label key={u.id}>
                    <input type="checkbox" checked={accessUserIds.includes(u.id)} onChange={() => toggleAccess(u.id)} />
                    <span className="courses__accessUser">
                      <span>
                        {u.firstname} {u.lastname} ({u.email})
                      </span>
                      {isMultiCourse && <span className="courses__badge">Admin — multi-courses autorisé</span>}
                      {!isMultiCourse && otherCourses.length > 0 && (
                        <span className="courses__badge courses__badge--warning">
                          Déjà affecté à : {otherCourses.join(', ')} — sera déplacé vers cette course
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
            <Button text="Save access" defaultStyle="primary" onClick={submitAccess} />
          </div>
        </Modal>
      )}

      {/* -- Modal duplication -------------------------------------------------- */}
      {duplicateModal && (
        <Modal onClose={() => setDuplicateModal(null)}>
          <div className="modal__content courses__modal">
            <h2>Dupliquer « {duplicateModal.name} »</h2>
            <InputLabel label="Name" name="name">
              <Input name="name" type="string" onChange={value => setDuplicateForm({ ...duplicateForm, name: value })} value={duplicateForm.name} />
            </InputLabel>
            <InputLabel label="Code" name="code">
              <Input name="code" type="string" onChange={value => setDuplicateForm({ ...duplicateForm, code: value })} value={duplicateForm.code} />
            </InputLabel>
            <InputLabel label="Year" name="year">
              <Input name="year" type="string" onChange={value => setDuplicateForm({ ...duplicateForm, year: value })} value={String(duplicateForm.year ?? '')} />
            </InputLabel>
            <p>Éléments à copier :</p>
            <div className="courses__accessList">
              {DUPLICATE_GROUPS.map(group => (
                <label key={group.key}>
                  <input type="checkbox" checked={!!duplicateGroups[group.key]} onChange={() => toggleGroup(group.key)} />
                  <span>{group.label}</span>
                </label>
              ))}
            </div>
            {!duplicateGroups.checklists &&
              (duplicateGroups.stages || duplicateGroups.flights || duplicateGroups.airports) && (
                <p className="courses__hint">
                  Sans les modèles de checklist, les stages / vols / aéroports copiés n'auront aucun modèle lié
                  (les liens sont vidés pour éviter une référence vers l'ancienne course).
                </p>
              )}
            <Button text="Duplicate" defaultStyle="primary" onClick={submitDuplicate} />
          </div>
        </Modal>
      )}

      {/* -- Confirmation suppression -------------------------------------------- */}
      {confirmDelete && (
        <ConfirmDelete
          onClose={() => setConfirmDelete(null)}
          list={[confirmDelete]}
          onConfirm={onDelete}
          formatList={data => `Course : ${data.name}`}
        />
      )}
    </div>
  );
};

export default Courses;
